// src/lib/api.ts
// ─────────────────────────────────────────────────────────────────────────────
// Thin PULSE API client. No SDK package yet (that's v0.8.1) — the standalone
// web app talks to the REST API directly through this module.
//
// Security posture: the S2S token lives in memory only (never localStorage /
// cookies), held by the closure below. The auth layer sets it after exchanging
// a Firebase ID token at /api/pulse/auth/exchange.
// ─────────────────────────────────────────────────────────────────────────────

import { PULSE_API_BASE } from "./env";
import type {
  Me,
  Recommendations,
  BodyMetric,
  Program,
  Goal,
  PeptidesResponse,
  Participant,
  AuditEntry,
  Page,
  AdminOverride,
  ExchangeResult,
} from "./types";

// In-memory token. Deliberately module-scoped (not exported) so nothing can
// read it back out; callers go through the fetch wrapper.
let serviceToken: string | null = null;

export function setServiceToken(token: string | null): void {
  serviceToken = token;
}

export function hasServiceToken(): boolean {
  return serviceToken !== null;
}

// Read the current in-memory token. Used by the PULSE SDK's getToken accessor
// so SDK-backed surfaces share the same token as the direct API client. The
// token still never touches storage — it lives only in this module's closure.
export function getServiceToken(): string | null {
  return serviceToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function url(path: string): string {
  // path starts with /api/pulse/... ; PULSE_API_BASE is "" for same-origin.
  return `${PULSE_API_BASE}${path}`;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  const auth = opts.auth ?? true;
  if (auth) {
    if (!serviceToken) throw new ApiError(401, "not authenticated");
    headers["authorization"] = `Bearer ${serviceToken}`;
  }
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  const res = await fetch(url(path), {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : res.statusText) || "request failed";
    throw new ApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

// ── Auth ───────────────────────────────────────────────────────────────────────
/** Exchange a Firebase ID token for an S2S token. Public (no S2S required). */
export function exchangeFirebaseToken(idToken: string): Promise<ExchangeResult> {
  return request<ExchangeResult>("POST", "/api/pulse/auth/exchange", {
    body: { idToken },
    auth: false,
  });
}

// ── User data ────────────────────────────────────────────────────────────────
export const api = {
  me: () => request<Me>("GET", "/api/pulse/me"),

  // Engine status (onboarding state + completion). The full message thread is a
  // server-only field, so the read-only conversations view surfaces status only
  // until a sanitized thread-read endpoint ships.
  chatStatus: () => request<Record<string, unknown>>("GET", "/api/pulse/status"),

  recommendations: () =>
    request<Recommendations>("GET", "/api/pulse/recommendations"),

  bodyMetrics: (q: { from?: string; to?: string; source?: string } = {}) => {
    const params = new URLSearchParams();
    if (q.from) params.set("from", q.from);
    if (q.to) params.set("to", q.to);
    if (q.source) params.set("source", q.source);
    const qs = params.toString();
    return request<{ items: BodyMetric[] }>(
      "GET",
      `/api/pulse/body-metrics${qs ? `?${qs}` : ""}`
    );
  },

  createBodyMetric: (body: Partial<BodyMetric> & { date: string }) =>
    request<BodyMetric>("POST", "/api/pulse/body-metrics", { body }),

  programs: () => request<{ items: Program[] }>("GET", "/api/pulse/programs"),

  goals: () => request<{ items: Goal[] }>("GET", "/api/pulse/goals"),

  peptides: () => request<PeptidesResponse>("GET", "/api/pulse/peptides"),

  createPeptideDose: (body: {
    peptide: string;
    doseMg: number;
    takenAt: string;
    planId?: number;
    site?: string;
    notes?: string;
  }) => request<unknown>("POST", "/api/pulse/peptides/doses", { body }),

  // ── Admin ──────────────────────────────────────────────────────────────────
  adminParticipants: (q: { limit?: number; before?: number; sourceApp?: string } = {}) => {
    const params = new URLSearchParams();
    if (q.limit) params.set("limit", String(q.limit));
    if (q.before) params.set("before", String(q.before));
    if (q.sourceApp) params.set("sourceApp", q.sourceApp);
    const qs = params.toString();
    return request<Page<Participant>>(
      "GET",
      `/api/pulse/admin/participants${qs ? `?${qs}` : ""}`
    );
  },

  adminAuditLog: (q: { limit?: number; before?: number; actorId?: string; action?: string } = {}) => {
    const params = new URLSearchParams();
    if (q.limit) params.set("limit", String(q.limit));
    if (q.before) params.set("before", String(q.before));
    if (q.actorId) params.set("actorId", q.actorId);
    if (q.action) params.set("action", q.action);
    const qs = params.toString();
    return request<Page<AuditEntry>>(
      "GET",
      `/api/pulse/admin/audit-log${qs ? `?${qs}` : ""}`
    );
  },

  adminCreateOverride: (body: {
    targetType: "trust_profile" | "guardian_link" | "participant";
    targetId: number;
    field: string;
    reason: string;
  }) => request<AdminOverride>("POST", "/api/pulse/admin/overrides", { body }),
};
